import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Button,
  Drawer,
  Flex,
  Image,
  LoadingOverlay,
  Text,
} from "@mantine/core";
import { usePrevious } from "@mantine/hooks";
import map from "lodash/map";
import orderBy from "lodash/orderBy";
import { type ReactNode, useCallback, useEffect, useMemo } from "react";

import { downloadPdf, viewPdf } from "@/app/api/screeners";
import AccountMenu from "@/components/accountMenu";
import { HighlightMenu } from "@/components/highlight-menu";
import {
  FitSummary,
  KeyAnswers,
  QuestionTranscript,
  ResultsSection,
  ScreenerDetails,
} from "./sections";
import { CompetencyBadge, INFO, UpwageBadge } from "@/components/upwage-badge";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import {
  useCandidateScreeningsQuery,
  useHighlightScreenerSessionQuery,
  useMarkSessionViewedQuery,
} from "@/hooks/queries/screenings.queries";
import { CompetencyFit, MessageSenders } from "@/types/screeners";
import type { Nullable } from "@/types/utils";
import { getCompetencyBadgeStyles } from "@/utils/competency";
import { TestID } from "@/utils/helpers";

interface SessionResultsProps {
  email?: string;
  navigationButtons?: ReactNode;
  onClose: () => void;
  opened: boolean;
  sessionId?: string;
}

const SESSION_RESULTS_TEST_ID = new TestID("session-results", "drawer");

export const SessionResults = ({
  email,
  navigationButtons,
  onClose,
  opened,
  sessionId,
}: SessionResultsProps) => {
  const track = useTrackAnalytics();
  const { mutate: mutateHighlight } = useHighlightScreenerSessionQuery();
  const { mutate: mutateSessionViewed } = useMarkSessionViewedQuery();
  const { data, isPending } = useCandidateScreeningsQuery(email, sessionId);
  const screenings = useMemo(
    () => data?.screenerSessions ?? [],
    [data?.screenerSessions],
  );
  const activeScreening = useMemo(
    () => screenings.find((screening) => screening.sessionId === sessionId),
    [screenings, sessionId],
  );
  const previousActiveScreening = usePrevious(activeScreening);

  const onDownloadPdfClick = useCallback(() => {
    if (activeScreening) {
      track("Download PDF Clicked", {
        screener_session_id: activeScreening.sessionId,
      });

      downloadPdf(activeScreening?.pdfLocation || "");
    }
  }, [activeScreening, track]);

  const onViewPdfClick = useCallback(() => {
    if (activeScreening) {
      track("View PDF Clicked", {
        screener_session_id: activeScreening.sessionId,
      });

      viewPdf(activeScreening?.pdfLocation || "");
    }
  }, [activeScreening, track]);

  const handleHighlight = async (value: Nullable<string>) => {
    if (activeScreening) {
      track("Candidate Highlighted", {
        highlight_color: value || "None",
        screener_session_id: activeScreening.sessionId,
      });

      mutateHighlight({ id: activeScreening.sessionId, value });
    }
  };

  useEffect(() => {
    const currentSessionIsUnviewed =
      activeScreening &&
      !activeScreening.viewedAt &&
      activeScreening.sessionId !== previousActiveScreening?.sessionId;
    const currentSessionIsIncomplete = !!sessionId && !email;

    // This will fire on every render for sessions without an email. As there
    // is nothing the user can actually do or interact with when there is no
    // email this shouldn't be a problem. This is due to needing to rely on
    // email for the query, without a provided email we have no way to know
    // whether the session has been viewed or not.
    if (currentSessionIsUnviewed || currentSessionIsIncomplete) {
      // Safe to cast, TypeScript is just losing the thread. As long as one of
      // the above is true the following will always result in a string.
      mutateSessionViewed((activeScreening?.sessionId ?? sessionId) as string);
    }
  }, [
    activeScreening,
    email,
    mutateSessionViewed,
    previousActiveScreening,
    sessionId,
  ]);

  return (
    <Drawer
      classNames={{
        body: "flex flex-col grow p-0 max-w-full",
        content: "flex",
      }}
      onClose={onClose}
      opened={opened}
      overlayProps={{ backgroundOpacity: 0.1, blur: 0 }}
      position="right"
      shadow="xl"
      size="lg"
      withCloseButton={false}
    >
      <LoadingOverlay visible={Boolean(email && isPending)} />

      <Flex align="center" className="p-4 w-full" justify="space-between">
        <Image src="/assets/logo.svg" alt="Logo" h={20} />
        <ActionIcon
          aria-label="close"
          data-testid={
            SESSION_RESULTS_TEST_ID.addComponent({
              element: "close",
            }).id
          }
          onClick={onClose}
          variant="transparent"
        >
          <Image src="/assets/circle-x.svg" alt="Logo" h={20} />
        </ActionIcon>
      </Flex>

      <Flex className="border-b border-t p-4">
        <Flex className="gap-2">
          <Button
            data-testid={
              SESSION_RESULTS_TEST_ID.addComponent({
                element: "download-pdf",
              }).id
            }
            disabled={!activeScreening?.pdfLocation}
            onClick={onDownloadPdfClick}
            rightSection={<FontAwesomeIcon icon={faDownload} />}
            size="sm"
          >
            Download PDF
          </Button>
          <Button
            className="mr-2"
            data-testid={
              SESSION_RESULTS_TEST_ID.addComponent({
                element: "view-pdf",
              }).id
            }
            disabled={!activeScreening?.pdfLocation}
            onClick={onViewPdfClick}
            rightSection={<FontAwesomeIcon icon={faDownload} />}
            size="sm"
            variant="outline"
          >
            View PDF
          </Button>
          <HighlightMenu
            activeIndicatorColor={activeScreening?.highlightColor}
            onChange={handleHighlight}
            disabled={!activeScreening}
          />
        </Flex>
        <AccountMenu />
      </Flex>

      {!email && (
        <Flex align="center" className="grow w-full" justify="center">
          <Text>Incomplete Screening Session</Text>
        </Flex>
      )}

      {activeScreening && (
        <Flex className="gap-2 px-4 py-3" direction="column">
          <Flex direction="column">
            <Flex align="center" className="w-full" justify="space-between">
              <Text className="font-semibold" size="xl">
                {activeScreening.candidateName}
              </Text>
              {activeScreening.screenerVersionNumber && (
                <UpwageBadge colorVariant={INFO} filled>
                  {`V${activeScreening.screenerVersionNumber}`}
                </UpwageBadge>
              )}
            </Flex>
            <Text className="font-semibold" size="sm">
              {activeScreening.candidateEmail} |{" "}
              {activeScreening.candidatePhone}
            </Text>
          </Flex>

          <ScreenerDetails screening={activeScreening} />

          <FitSummary screening={activeScreening} />

          <KeyAnswers screening={activeScreening} />

          {activeScreening.overallFit &&
            activeScreening.overallFit !== CompetencyFit.NotAsked && (
              <ResultsSection
                style={{
                  // Background of the overall fit section matches the badge
                  // background, but with lightness set to 95%
                  backgroundColor: `hsl(from ${
                    getCompetencyBadgeStyles(activeScreening.overallFit, true)
                      .background
                  } h s 95%)`,
                }}
                title="Overall fit"
                titleBadge={
                  <CompetencyBadge
                    isOverallFit
                    score={activeScreening.overallFit}
                  />
                }
              >
                <Text c="gray.9" size="sm">
                  Overall fit combines all the competencies below. Competencies
                  are set by company leaders to predict retention, performance,
                  and fit. We welcome feedback on competencies and encourage you
                  to dive into the quotes that speak to each one.
                </Text>
              </ResultsSection>
            )}

          {activeScreening.competencies &&
            map(activeScreening.competencies, ({ quote, score }, key) => (
              <ResultsSection
                key={key}
                title={key}
                titleBadge={score && <CompetencyBadge score={score} />}
              >
                <Text c="gray.9" size="sm">
                  {quote || "No available quote for this competency."}
                </Text>
              </ResultsSection>
            ))}

          {activeScreening.basicQuestions &&
            activeScreening.basicQuestions.length > 0 && (
              <QuestionTranscript
                sessionId={activeScreening.sessionId}
                title="Basic Questions"
                transcript={orderBy(
                  activeScreening.basicQuestions,
                  "questionOrder",
                ).map(({ message, qualifyingPassed, sender }) => ({
                  message,
                  name: sender,
                  rightSection:
                    qualifyingPassed === null ? null : (
                      <UpwageBadge
                        colorVariant={
                          qualifyingPassed
                            ? CompetencyFit.VeryHigh
                            : CompetencyFit.VeryLow
                        }
                      >
                        {qualifyingPassed ? "PASSED" : "FAILED"}
                      </UpwageBadge>
                    ),
                }))}
              />
            )}
          {activeScreening.voiceQuestions &&
            activeScreening.voiceQuestions.length > 0 && (
              <QuestionTranscript
                sessionId={activeScreening.sessionId}
                title="Voice Questions"
                transcript={activeScreening.voiceQuestions.map(
                  ({ message, sender }) => ({
                    isVoiceResponse:
                      sender === MessageSenders.Candidate && message !== "Skip",
                    message,
                    name: sender,
                  }),
                )}
              />
            )}
          {activeScreening.assessmentQuestions &&
            activeScreening.assessmentQuestions.length > 0 && (
              <QuestionTranscript
                sessionId={activeScreening.sessionId}
                title="Assessment Questions"
                transcript={activeScreening.assessmentQuestions.map(
                  ({ message, sender }) => ({
                    message,
                    name: sender,
                  }),
                )}
              />
            )}
        </Flex>
      )}

      {navigationButtons}
    </Drawer>
  );
};
